import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';

import { createRule as createRuleApi } from '../../../services/ruleService';
import { mergeActiveRules } from './ruleEngine';
import { updateTab } from './workspaceTabUtils';

/**
 * A rule lives in one of three places, and can be moved between the last two:
 *
 *   api    — saved on the server, available to every workspace
 *   global — this workspace, every tab
 *   tab    — this tab only
 *
 * The widgets hand back a rule carrying a `scope`, or one of the two move
 * markers. Each destination gets its own function below so the dispatcher reads
 * as the table it is.
 */
export default function useWorkspaceRules({
  layoutConfig,
  activeTab,
  updateLayoutAndPersist,
  apiRules,
  setApiRules,
}) {
  const tabRules = useMemo(() => activeTab?.rules || [], [activeTab]);
  const tabActiveRuleIds = useMemo(() => activeTab?.activeRuleIds || [], [activeTab]);

  /** Every rule a widget could show, from all three scopes, first definition wins. */
  const allRulesPool = useMemo(() => {
    const pool = [...apiRules];
    for (const rule of [...(layoutConfig.globalRules || []), ...tabRules]) {
      if (!pool.find((p) => p.id === rule.id)) pool.push(rule);
    }
    return pool;
  }, [apiRules, layoutConfig.globalRules, tabRules]);

  const activeRules = useMemo(
    () => mergeActiveRules(layoutConfig.globalActiveRuleIds || [], tabActiveRuleIds, allRulesPool),
    [layoutConfig.globalActiveRuleIds, tabActiveRuleIds, allRulesPool]
  );

  const promoteToWorkspace = useCallback((rule) => {
    const promoted = { ...rule, scope: 'global' };
    delete promoted._promoteFromTab;
    const withoutFromTab = updateTab(layoutConfig, activeTab.id, {
      rules: tabRules.filter((r) => r.id !== rule.id),
      activeRuleIds: tabActiveRuleIds.filter((id) => id !== rule.id),
    });
    updateLayoutAndPersist({
      ...withoutFromTab,
      globalRules: [...(withoutFromTab.globalRules || []), promoted],
      globalActiveRuleIds: [...(withoutFromTab.globalActiveRuleIds || []), promoted.id],
    });
  }, [layoutConfig, activeTab, tabRules, tabActiveRuleIds, updateLayoutAndPersist]);

  const demoteToTab = useCallback((rule) => {
    const demoted = { ...rule, scope: 'tab' };
    delete demoted._demoteFromGlobal;
    const withoutFromWorkspace = {
      ...layoutConfig,
      globalRules: (layoutConfig.globalRules || []).filter((r) => r.id !== rule.id),
      globalActiveRuleIds: (layoutConfig.globalActiveRuleIds || []).filter((id) => id !== rule.id),
    };
    updateLayoutAndPersist(updateTab(withoutFromWorkspace, activeTab.id, {
      rules: [...tabRules, demoted],
      activeRuleIds: [...tabActiveRuleIds, demoted.id],
    }));
  }, [layoutConfig, activeTab, tabRules, tabActiveRuleIds, updateLayoutAndPersist]);

  const createOnServer = useCallback(async (rule) => {
    try {
      const saved = await createRuleApi(rule);
      setApiRules((prev) => [...prev, saved]);
      updateLayoutAndPersist({
        ...layoutConfig,
        globalActiveRuleIds: [...(layoutConfig.globalActiveRuleIds || []), saved.id],
      });
      toast.success('Rule created');
    } catch (err) {
      toast.error('Failed to create rule');
    }
  }, [layoutConfig, setApiRules, updateLayoutAndPersist]);

  const addWorkspaceRule = useCallback((rule) => {
    updateLayoutAndPersist({
      ...layoutConfig,
      globalRules: [...(layoutConfig.globalRules || []), rule],
      globalActiveRuleIds: [...(layoutConfig.globalActiveRuleIds || []), rule.id],
    });
  }, [layoutConfig, updateLayoutAndPersist]);

  const addTabRule = useCallback((rule) => {
    updateLayoutAndPersist(updateTab(layoutConfig, activeTab.id, {
      rules: [...tabRules, rule],
      activeRuleIds: [...tabActiveRuleIds, rule.id],
    }));
  }, [layoutConfig, activeTab, tabRules, tabActiveRuleIds, updateLayoutAndPersist]);

  const handleCreateRule = useCallback(async (rule) => {
    // Moving a rule needs a tab to move it to or from.
    if (rule._promoteFromTab) return activeTab ? promoteToWorkspace(rule) : undefined;
    if (rule._demoteFromGlobal) return activeTab ? demoteToTab(rule) : undefined;

    if (rule.scope === 'api' && !rule.workspaceOnly) return createOnServer(rule);
    if (rule.scope === 'global') return addWorkspaceRule(rule);
    // Everything else — including an api-scoped rule marked workspaceOnly — is
    // a tab rule, and needs a tab.
    return activeTab ? addTabRule(rule) : undefined;
  }, [activeTab, promoteToWorkspace, demoteToTab, createOnServer, addWorkspaceRule, addTabRule]);

  const handleToggleGlobalRule = useCallback((rule) => {
    const ids = layoutConfig.globalActiveRuleIds || [];
    updateLayoutAndPersist({
      ...layoutConfig,
      globalActiveRuleIds: ids.includes(rule.id) ? ids.filter((id) => id !== rule.id) : [...ids, rule.id],
    });
  }, [layoutConfig, updateLayoutAndPersist]);

  const makeToggleTabRule = useCallback((tabId) => (rule) => {
    const tab = (layoutConfig.tabs || []).find((t) => t.id === tabId);
    if (!tab) return;
    const ids = tab.activeRuleIds || [];
    updateLayoutAndPersist(updateTab(layoutConfig, tabId, {
      activeRuleIds: ids.includes(rule.id) ? ids.filter((id) => id !== rule.id) : [...ids, rule.id],
    }));
  }, [layoutConfig, updateLayoutAndPersist]);

  return { allRulesPool, activeRules, handleCreateRule, handleToggleGlobalRule, makeToggleTabRule };
}
